import { calculateLeadTime } from '@calculations/lead-time/calculate';
import { transformLeadTimeConfigToLeadTimeStrategy } from '@calculations/lead-time/utilities/transform-config';
import { LeadTimeStrategy } from '@snap-types/models/enums';
import { SnapSuggestionStatus } from '@snap-types/models/sort-status';
import { Decimal } from '@snap-types/types/decimal';
import { WritableQuoteBuilderStateType } from '@ui/features/quote-builder/types/state';

import {
  getQuoteLineOrThrow,
  getQuotePriceOrThrow,
} from '../../utilities/action-guards';

/**
 * Applies a user-entered per-price lead time (in days). The value is recorded as
 * both the current and the user override so it survives recalculation and marks
 * the price's lead time as manually set.
 */
export function onPartLeadTimeChange(
  state: WritableQuoteBuilderStateType,
  params: {
    quoteLineUuid: string;
    quotePriceUuid: string;
    leadTimeWeeks: number;
  },
) {
  const quotePrice = getQuotePriceOrThrow(
    state,
    params.quoteLineUuid,
    params.quotePriceUuid,
  );

  quotePrice.leadTimeWeeks = params.leadTimeWeeks;
  quotePrice.userLeadTimeWeeks = params.leadTimeWeeks;

  syncLeadTimeSuggestionStatus(state, params.quoteLineUuid);
}

/**
 * The line's lead time suggestion is still "accepted" while no price carries a
 * user override that departs from what we calculated. A line that departs from
 * its suggestions can no longer be automated.
 *
 * EXCLUDED lines (imports, alpha quotes, and other flows with no calculated
 * lead time to suggest) stay excluded — the metrics treat that as a distinct
 * state, not as a rejection.
 */
function syncLeadTimeSuggestionStatus(
  state: WritableQuoteBuilderStateType,
  quoteLineUuid: string,
) {
  const quoteLine = getQuoteLineOrThrow(state, quoteLineUuid);

  if (quoteLine.leadTimeSuggestionStatus === SnapSuggestionStatus.EXCLUDED) {
    return;
  }

  const allAccepted = Object.values(quoteLine.quotePrices).every(
    (quotePrice) =>
      // A cleared override is always accepted; we compare the override itself
      // rather than the effective lead time so that a recalculated price is not
      // read as a rejection when `automatedLeadTimeWeeks` is a stale baseline.
      quotePrice.userLeadTimeWeeks == null ||
      quotePrice.userLeadTimeWeeks === quotePrice.automatedLeadTimeWeeks,
  );

  quoteLine.leadTimeSuggestionStatus = allAccepted
    ? SnapSuggestionStatus.ACCEPTED
    : SnapSuggestionStatus.REJECTED;

  if (!allAccepted) {
    quoteLine.automatable = false;
  }
}

/**
 * Recomputes a price's lead time for a new quantity when the line uses the
 * quantity-aware strategy. Other strategies produce a quantity-independent lead
 * time, so this is a no-op for them. Any manual override is cleared, mirroring
 * how a quantity change re-derives the unit price.
 */
export function recalculateQuantityAwareLeadTime(
  state: WritableQuoteBuilderStateType,
  params: {
    quoteLineUuid: string;
    quotePriceUuid: string;
    quantity: Decimal;
  },
) {
  const quoteLine = getQuoteLineOrThrow(state, params.quoteLineUuid);
  const { leadTimeConfig, quantityAwareInputs } = quoteLine;

  if (
    leadTimeConfig?.leadTimeType !== LeadTimeStrategy.QUANTITY_AWARE ||
    !quantityAwareInputs
  ) {
    return;
  }

  const quotePrice = getQuotePriceOrThrow(
    state,
    params.quoteLineUuid,
    params.quotePriceUuid,
  );

  const { weeks } = calculateLeadTime(
    params.quantity,
    transformLeadTimeConfigToLeadTimeStrategy(leadTimeConfig, state),
    {
      partId: quoteLine.partId ?? '',
      planningLeadTime: null,
      quantityAwareInputs,
    },
  );

  quotePrice.leadTimeWeeks = weeks;
  quotePrice.userLeadTimeWeeks = null;

  // Clearing the override can put the line back in step with its suggestions.
  syncLeadTimeSuggestionStatus(state, params.quoteLineUuid);
}
